import { motion, HTMLMotionProps } from 'framer-motion';
import { forwardRef } from 'react';
import { useMobilePerformance } from '../../hooks/useMobilePerformance';

type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'white';
type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-primary hover:bg-primary-dark text-white shadow-lg shadow-primary/30 hover:shadow-primary/50',
  outline: 'border-2 border-primary text-primary hover:bg-primary/10 hover:border-primary-dark',
  ghost: 'text-text-primary-light dark:text-text-primary-dark hover:bg-black/5 dark:hover:bg-white/5',
  white: 'bg-white text-primary hover:bg-gray-100 shadow-lg hover:shadow-xl',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg font-bold',
  xl: 'px-10 py-5 text-xl font-bold',
};

interface ButtonProps extends Omit<HTMLMotionProps<"button">, 'size'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  children: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  className = '', 
  loading = false,
  disabled,
  ...props 
}, ref) => {
  const { shouldReduceAnimations } = useMobilePerformance();
  
  const isDisabled = disabled || loading;
  
  return (
    <motion.button
      ref={ref}
      whileHover={shouldReduceAnimations || isDisabled ? undefined : { 
        scale: 1.02,
        y: -1
      }}
      whileTap={shouldReduceAnimations || isDisabled ? undefined : { 
        scale: 0.96, // More tactile for mobile
        opacity: 0.9 
      }}
      transition={{ 
        type: "spring",
        stiffness: 400,
        damping: 17
      }}
      className={`
        relative rounded-xl font-bold transition-all duration-300 group
        flex items-center justify-center gap-2
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
        ${variants[variant]}
        ${sizes[size]}
        ${isDisabled ? 'opacity-50 cursor-not-allowed grayscale-[0.5]' : 'cursor-pointer'}
        ${className}
      `}
      disabled={isDisabled}
      aria-busy={loading}
      {...props}
    >
      {/* Premium Shine Overlay */}
      {!isDisabled && (
        <span className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
          <motion.span 
            initial={{ left: '-100%' }}
            whileHover={{ left: '100%' }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="absolute top-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-20deg]" 
          />
        </span>
      )}

      {/* Primary variant specific glow */}
      {variant === 'primary' && !isDisabled && (
        <div className="absolute inset-0 rounded-xl bg-primary/20 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      )}
      
      {/* Content with loading state */}
      <span className={`relative z-10 flex items-center gap-2 ${loading ? 'opacity-0' : ''}`}>
        {children}
      </span>
      
      {/* Loading spinner */}
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center z-10">
          <span className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </span>
      )}
    </motion.button>
  );
});

Button.displayName = 'Button';

export default Button;

